import { service } from '@ember/service';
import { DEBUG } from '@glimmer/env';

import { LOCALE_CODES, LOCALES } from '@repo/shared-config/constants/locales';
import IntlService from 'ember-intl/services/intl';

import CURRENCIES from 'register-partners-js/constants/currencies';

export const defaultMoneyOptions = {
  minimumFractionDigits: 2,
  currency: CURRENCIES.default,
};

let optionsToIgnore = new Set(['htmlSafe', 'locale']);

/**
 * Replaces the `{` and `}` characters with `(` and `)` so that these
 * aren't interpreted as variables.
 *
 * Replaces the `\"` characters with `"` to allow additional escapes.
 * See https://github.com/ember-intl/ember-intl/issues/1035.
 */
function replaceInterpolators(input) {
  return String(input).replace(/\{/g, '(').replace(/\}/g, ')').replace(/\\"/g, '"');
}

function stringify(object) {
  return JSON.stringify(object, Object.keys(object).sort());
}

function stringifyOptions(options) {
  let filteredOptions = {};
  for (let [key, value] of Object.entries(options || {})) {
    if (optionsToIgnore.has(key)) {
      continue;
    }
    filteredOptions[key] = value;
  }
  return replaceInterpolators(stringify(filteredOptions));
}

/**
 * Returns the key and options in a way that helps end-developers
 * write tests.
 *
 * @private
 * @hide
 */
function _onMissingTranslation(key, _locales, options) {
  return `t:${key}:${stringifyOptions(options)}`;
}

export default class extends IntlService {
  @service zendeskWidget;
  @service launchdarkly;
  @service sentry;

  getLocales = LOCALES;
  getLocaleCodes = LOCALE_CODES;

  // this is a proxy to the old _ee method present in v6 to allow
  // react-migration-toolkit/src/react/hooks/providers/use-ember-intl.ts
  // https://gitlab.qonto.co/npm-packages/react-migration-toolkit/-/blob/main/react-migration-toolkit/src/react/hooks/providers/use-ember-intl.ts#L11
  _ee = this._eventEmitter;

  // This is the default locale for the app, this has been changed in ember-intl v7.0.0
  // in order to maintain standard behaviour has been remapped during migration from v6 to v7
  get locale() {
    if (DEBUG) {
      // oxlint-disable-next-line no-console
      console.warn('locale is deprecated, use intl.primaryLocale instead');
    }
    return this._locale;
  }

  // This is a proxy to an ember-intl internal property, this has been changed in ember-intl v7.0.0
  // https://ember-intl.github.io/ember-intl/docs/migration/v7#minimized-api-of-the-intl-service
  translationsFor(...args) {
    if (DEBUG) {
      // oxlint-disable-next-line no-console
      console.warn('translationsFor is deprecated');
    }
    return this._intls[args[0]].messages;
  }

  // https://ember-intl.github.io/ember-intl/docs/guide/migration/v7#minimized-api-of-the-intl-service
  lookup(translationKey) {
    let translation = this.getTranslation(translationKey, this.primaryLocale);

    if (translation?.includes('Missing translation')) {
      this.sentry.captureException(new Error(translation));
    }
    return translation;
  }

  onMissingTranslation(key, _locale, options) {
    let missingError = _onMissingTranslation(key, _locale, options);
    this.sentry.captureException(missingError);

    if (DEBUG) {
      return missingError;
    } else {
      return '';
    }
  }

  formatMoney(money, options) {
    let amount = money || 0;

    let opts = Object.assign({}, defaultMoneyOptions, options);
    let customFormatting = opts.style !== 'currency';
    let { digit } = opts;
    if (digit) {
      opts.minimumFractionDigits = Number(digit);
    }
    let { signus } = opts;
    let formatted = this.formatNumber(customFormatting ? amount : Math.abs(amount), opts);

    if (customFormatting) {
      formatted += ' ' + opts.currency;
    }

    if (signus) {
      if (typeof opts.signus === 'boolean') {
        signus = amount < 0 ? '-' : '+';
      }

      formatted = `${signus} ${formatted}`;
    }

    return formatted;
  }
}
